// 管理頁右邊的「機關」分頁（只有製作看得到）
//   上面：這個 cue 的機關位置（常用位置一鍵、滑桿、打數字、回到沿用；延遲和走幾秒；被下一個 cue 接手的說明）
//   下面：設定機關（新增 / 編輯 / 刪除 / 排序；表單直接展開在這裡，3D 畫面照樣能轉、能選物件）
//   匯出機關總表（CSV，用 Excel 打開）
import { useEffect, useMemo, useRef, useState } from 'react';
import { Anchor, ArrowDown, ArrowUp, Cable, ChevronDown, ChevronRight, Cog, FileSpreadsheet, Info, LoaderCircle, Pencil, Plus, Rows3, TriangleAlert, Waves } from 'lucide-react';
import Icon from '../Icon';
import type { Cue, StageObject } from '../../lib/api';
import type { EditorMechanisms } from '../../lib/mechanisms/useMechanisms';
import { formatMechValue } from '../../lib/mechanisms/motion';
import type { Mechanism, MechanismGroup, MechVec3 } from '../../lib/mechanisms/model';
import { flipLabel } from '../../lib/mechanisms/flip';
import { countSides } from '../../lib/mechanisms/split';
import { layoutMechanisms, moveLayoutItem, valueRange } from '../../lib/mechanisms/group';
import { analyzeHoist, hoistWarnings, tiltText } from '../../lib/mechanisms/hoist';
import { formatFieldNumber, parseNumberInput } from '../../lib/numberField';
import * as mapi from '../../lib/mechanisms/mechanismApi';
import { buildMechanismSheetCsv, downloadMechanismSheet, sheetFileName } from '../../lib/mechanisms/cueSheet';
import { toast } from '../../lib/toast';
import { confirmDialog } from '../dialogs';
import MechanismCueRow from './MechanismCueRow';
import { GroupTitle, Tiles, SHAPE_ICON } from '../adjust/MechList';
import { mechShape, panelGroups, tileLabels, type PanelGroup } from '../../lib/adjust/mechGroups';
import MechanismSetupForm from './MechanismSetupForm';
import { askUnsplitNames } from './MechanismUnsplitDialog';
import MechanismBatchForm from './MechanismBatchForm';
import MechanismGroupForm from './MechanismGroupForm';
import MechanismHoistForm from './MechanismHoistForm';
import MechanismPatternPanel from './MechanismPatternPanel';
import HelpLink from '../HelpLink';
import { useTip } from '../adjust/AdjustUi';
import '../adjust/AdjustStage.css';
import { HELP_ANCHORS } from '../../lib/helpLinks';
import './mechanisms.css';

interface Props {
  mech: EditorMechanisms;
  projectId: string;
  projectName?: string;
  /** 這首歌的 cue（正式 + 提案） */
  cues: Cue[];
  selectedCue: Cue | null;
  onSelectCue: (id: string | null) => void;
  stageObjects: StageObject[];
  selectedObjectIds: string[];
  scaleCorrection: number;
  /** 轉換舊位置後，物件位置也變了 → 重新讀 */
  onStatesChanged: () => void;
  /**
   * Cue 庫分頁裡的「機關」：看的是 Cue 庫的一個 cue（不屬於哪一首歌）。
   * 同一套列、延遲 / 走幾秒、花樣，只是標題寫 Cue 庫的編號、不給換 cue、不放「設定機關」（那是整個專案的，在右邊「機關」分頁）
   */
  library?: { label: string; name: string } | null;
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function MechanismTab({
  mech, projectId, projectName, cues, selectedCue, onSelectCue, stageObjects, selectedObjectIds, scaleCorrection, onStatesChanged, library = null,
}: Props) {
  const mechanisms = mech.mechanisms;
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [front, setFront] = useState<MechVec3 | null>(null);
  // 一排機關（v2 第 3 階段）：清單怎麼排、哪幾排展開、哪一排正在套花樣、哪一排在編輯、一次建立的表單
  const layout = useMemo(() => layoutMechanisms(mechanisms, mech.groups), [mechanisms, mech.groups]);
  const [openMechId, setOpenMechId] = useState<string | null>(null);         // 「這個 cue 的機關位置」展開控制的那一個機關（同時只開一個）
  const detailRef = useRef<HTMLUListElement | null>(null);
  const [openSetup, setOpenSetup] = useState<Set<string>>(() => new Set());   // 「設定機關」展開的排
  const [patternFor, setPatternFor] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  // 吊掛（v2 第 4 階段）：哪一組在編輯（'new' = 新增）、哪幾組的「這個 cue 的機關位置」收起來了、哪一組開著「一起動」
  const [hoistFormFor, setHoistFormFor] = useState<string | 'new' | null>(null);
  const [togetherFor, setTogetherFor] = useState<string | null>(null);
  const toggle = (set: Set<string>, id: string) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); return n; };
  // 在時間軸點到某一排裡的機關 → 這裡也把那一排展開，看得到是哪一個
  const focusedGroupId = mechanisms.find(m => m.id === mech.focusedId)?.groupId ?? null;
  useEffect(() => {
    if (focusedGroupId) {
      setOpenSetup(s => (s.has(focusedGroupId) ? s : new Set(s).add(focusedGroupId)));
    }
  }, [focusedGroupId, mech.focusedId]);
  // 3D 或時間軸選到某個機關 → 清單展開它、捲到它
  useEffect(() => {
    const m = mechanisms.find(x => x.id === mech.focusedId);
    if (m && m.variant !== 'hoist_rig') setOpenMechId(m.id);
  }, [mech.focusedId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (openMechId) detailRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [openMechId, mech.focusedId]);
  useEffect(() => { if (!selectedCue) { setPatternFor(null); setTogetherFor(null); } }, [selectedCue]);
  const masters = useMemo(() => cues.filter(c => c.status === 'master').sort((a, b) => a.order - b.order), [cues]);
  const cueName = useMemo(() => {
    const byId = new Map(cues.map(c => [c.id, c.name]));
    return (id: string) => byId.get(id) || '前面的 cue';
  }, [cues]);
  const cueLabel = useMemo(() => {
    const no = new Map(cues.filter(c => c.status === 'master').sort((a, b) => a.order - b.order).map((c, i) => [c.id, i + 1]));
    return (id: string) => (library && id !== selectedCue?.id ? `「${cueName(id)}」` : library ? `Cue ${library.label}「${library.name}」` : no.has(id) ? `Cue ${no.get(id)}「${cueName(id)}」` : `「${cueName(id)}」`);
  }, [cues, cueName, library, selectedCue?.id]);

  // 舞台正面（「往觀眾」「往左」、方向字）：模型讀好之後問一次；打開設定時再問一次
  useEffect(() => {
    const t = window.setTimeout(() => setFront(mech.sceneQueryRef.current?.stageFront() ?? null), 400);
    return () => window.clearTimeout(t);
  }, [stageObjects, editingId, mech.sceneQueryRef]);

  const libTip = useTip(library ? '預覽從預設位置開始動；在歌裡會從前一個 cue 的位置開始。延遲和走幾秒跟著這個 cue 存，改完按上面的「覆蓋」才會存進 Cue 庫。' : null, '機關位置');
  const editingDefault = !selectedCue;
  const cueCheck = selectedCue ? mech.check?.cues.get(selectedCue.id) : undefined;
  const masterIdx = selectedCue ? masters.findIndex(c => c.id === selectedCue.id) : -1;
  const title = library ? `Cue ${library.label}「${library.name}」`
    : !selectedCue
    ? '預設位置（沒選 cue）'
    : masterIdx >= 0 ? `Cue ${masterIdx + 1}「${selectedCue.name}」` : `「${selectedCue.name}」`;

  async function exportSheet() {
    setExporting(true);
    try {
      const data = await mapi.getMechanismSheet(projectId);
      if (data.mechanisms.length === 0) { toast.info('還沒有機關，沒有東西可以匯出'); return; }
      const csv = buildMechanismSheetCsv({ ...data, front });
      downloadMechanismSheet(csv, sheetFileName(projectName || '專案'));
      toast.success('已下載機關總表（用 Excel 打開）');
    } catch (e) {
      toast.error('匯出機關總表失敗：' + msg(e));
    } finally {
      setExporting(false);
    }
  }

  async function save(editing: Mechanism | null, input: mapi.MechanismPayload, moveObjects: boolean) {
    const inputName = String(input.name ?? editing?.name ?? '');
    setBusy(true);
    const send = async (move: boolean) => {
      if (editing) {
        const r = await mech.update(editing.id, { ...input, moveObjects: move || undefined });
        if (r.outOfRangeCount > 0) toast.warn(`有 ${r.outOfRangeCount} 個 cue 存的位置超出新的行程，播放時會停在邊界`);
      } else {
        const m = await mech.create({ ...input, moveObjects: move || undefined });
        mech.setFocusedId(m.id);
      }
    };
    try {
      try {
        await send(moveObjects);
      } catch (e) {
        const err = e as mapi.MechanismApiError;
        if (err.status !== 409 || !err.data?.conflicts?.length) throw e;
        if (!(await confirmDialog({ title: `要把它移到「${inputName}」嗎？`, message: err.message, confirmLabel: '移過來' }))) return;
        await send(true);
      }
      toast.success(editing ? `已儲存「${inputName}」` : `已新增「${inputName}」`);
      setEditingId(null);
      setHoistFormFor(null);
    } catch (e) {
      toast.error('機關沒有存到：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(m: Mechanism) {
    const ok = await confirmDialog({
      title: `刪除「${m.name}」？`,
      message: `會一起清掉 ${m.cueCount} 個 cue 裡設定的機關位置，不能復原。`,
      confirmLabel: '刪除',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await mech.remove(m.id);
      setEditingId(null);
      toast.success(`已刪除「${m.name}」`);
    } catch (e) {
      toast.error('刪除失敗：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  async function removeRig(rig: Mechanism, hoists: Mechanism[]) {
    const positions = hoists.reduce((n, h) => n + h.cueCount, 0);
    const ok = await confirmDialog({
      title: `刪除吊掛「${rig.name}」？`,
      message: `會一起刪掉它的 ${hoists.length} 個吊點，和 ${positions} 筆 cue 裡設定的吊點位置，不能復原。帶動的物件會留在原地。`,
      confirmLabel: '刪除',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await mech.remove(rig.id);
      setHoistFormFor(null);
      toast.success(`已刪除吊掛「${rig.name}」`);
    } catch (e) {
      toast.error('刪除失敗：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  async function convert(m: Mechanism) {
    setBusy(true);
    try {
      const dry = await mapi.convertCuePositions(projectId, m.id, true);
      if (dry.converted === 0) {
        toast.info(dry.skipped.length
          ? `沒有可以轉的 cue（${dry.skipped.length} 個 cue 的物件動法不一致）`
          : `「${m.name}」帶的物件在每個 cue 都沒有離開預設位置，不用轉`);
        return;
      }
      const skippedNote = dry.skipped.length ? `另外 ${dry.skipped.length} 個 cue 沒辦法轉，會照原本的樣子留著。` : '';
      const ok = await confirmDialog({
        title: `把 ${dry.converted} 個 cue 的位置轉成「${m.name}」的機關位置？`,
        message: `這些 cue 裡「${m.name}」帶的物件的高度 / 角度會變成機關的位置，畫面看起來不會變，之後就能用機關時間軸調整、匯出總表。想先試試看的話，可以先複製專案、在副本上試。${skippedNote}`,
        confirmLabel: '轉換',
      });
      if (!ok) return;
      const r = await mapi.convertCuePositions(projectId, m.id, false);
      toast.success(`已轉好 ${r.converted} 個 cue`);
      const bad = r.skipped.filter(s => s.reason === 'inconsistent');
      const range = r.skipped.filter(s => s.reason === 'range');
      const list = (xs: typeof r.skipped) => xs.slice(0, 8).map(s => s.name).join('、') + (xs.length > 8 ? '…' : '');
      if (bad.length) toast.warn(`${bad.length} 個 cue 的物件動法不一致，沒有轉：${list(bad)}`, 12000);
      if (range.length) toast.warn(`${range.length} 個 cue 轉出來會超出行程，沒有轉：${list(range)}`, 12000);
      await Promise.all([mech.refreshDefs(), mech.refreshPositions()]);
      onStatesChanged();
      setEditingId(null);
    } catch (e) {
      toast.error('轉換失敗：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  /** 對開拆成兩個各自動的機關：先問（說明 + 兩個名字可以改），再一次拆好，重新讀機關清單和位置 */
  async function unsplit(m: Mechanism) {
    const names = await askUnsplitNames(m, mechanisms);
    if (!names) return;
    setBusy(true);
    try {
      await mapi.unsplitMechanism(projectId, m.id, names);
      await Promise.all([mech.refreshDefs(), mech.refreshPositions()]);
      onStatesChanged();
      setEditingId(null);
      toast.success('已拆成兩個機關');
    } catch (e) {
      toast.error('拆開失敗：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  /** 清單上的一項（一個機關或一整排）上移 / 下移一格；一整排一起移，不會被拆開 */
  function move(index: number, dir: -1 | 1) {
    const ids = moveLayoutItem(layout, index, dir);
    if (ids) void mech.reorder(ids);
  }

  /** 一次建立 N 個機關（一排）；有物件已經在別的機關 → 問過再移過來 */
  async function saveBatch(payload: mapi.BatchPayload) {
    setBusy(true);
    try {
      let r;
      try {
        r = await mech.createBatch(payload);
      } catch (e) {
        const err = e as mapi.MechanismApiError;
        if (err.status !== 409 || !err.data?.conflicts?.length) throw e;
        const c = err.data.conflicts;
        const names = c.slice(0, 4).map(x => `「${x.objectName}」（在「${x.mechanismName}」）`).join('、') + (c.length > 4 ? `…等 ${c.length} 個` : '');
        const ok = await confirmDialog({
          title: `要把這些物件移到新的一排「${payload.namePrefix}」嗎？`,
          message: `${names} 已經在別的機關裡，一個物件只能直接放在一個機關。`,
          confirmLabel: '移過來',
        });
        if (!ok) return;
        r = await mech.createBatch({ ...payload, moveObjects: true });
      }
      toast.success(`已建立「${r.group.name}」：${r.mechanisms.length} 個機關`);
      setBatchOpen(false);
      setOpenSetup(s => new Set(s).add(r.group.id));
      if (r.mechanisms[0]) mech.setFocusedId(r.mechanisms[0].id);
    } catch (e) {
      toast.error('機關沒有建立：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveGroup(g: MechanismGroup, patch: mapi.GroupPatch) {
    setBusy(true);
    try {
      const r = await mech.updateGroup(g.id, patch);
      if (r.outOfRangeCount > 0) toast.warn(`有 ${r.outOfRangeCount} 個 cue 存的位置超出新的行程，播放時會停在邊界`);
      toast.success(`已儲存「${r.group.name}」`);
      setEditingGroupId(null);
    } catch (e) {
      toast.error('這一排沒有存到：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  async function dissolveGroup(g: MechanismGroup, count: number) {
    const ok = await confirmDialog({
      title: `解散「${g.name}」這一排？`,
      message: `${count} 個機關都會留著，變成一個一個普通機關；每個 cue 的位置和時間都不會變。`,
      confirmLabel: '解散',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await mech.removeGroup(g.id, true);
      setEditingGroupId(null);
      setPatternFor(null);
      toast.success(`已解散「${g.name}」，${count} 個機關還在`);
    } catch (e) {
      toast.error('解散失敗：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  async function deleteGroup(g: MechanismGroup, members: Mechanism[]) {
    const positions = members.reduce((n, m) => n + m.cueCount, 0);
    const ok = await confirmDialog({
      title: `刪除「${g.name}」和裡面的 ${members.length} 個機關？`,
      message: `會一起清掉 ${positions} 筆 cue 裡設定的機關位置，不能復原。只想拆開這一排、機關要留著的話，按「只解散這一排」。`,
      confirmLabel: '刪除',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await mech.removeGroup(g.id, false);
      setEditingGroupId(null);
      setPatternFor(null);
      toast.success(`已刪除「${g.name}」`);
    } catch (e) {
      toast.error('刪除失敗：' + msg(e));
    } finally {
      setBusy(false);
    }
  }

  const children = (m: Mechanism) => mechanisms.filter(x => x.parentId === m.id).map(x => x.name);
  const formFor = (m: Mechanism | null) => (
    <MechanismSetupForm
      key={m?.id ?? 'new'}
      editing={m}
      mechanisms={mechanisms}
      stageObjects={stageObjects}
      selectedObjectIds={selectedObjectIds}
      scaleCorrection={scaleCorrection}
      front={front ?? { x: 0, y: 0, z: 1 }}
      groupCenter={(ids) => mech.sceneQueryRef.current?.groupCenter(ids) ?? null}
      groupBox={(ids) => mech.sceneQueryRef.current?.groupBox(ids) ?? null}
      busy={busy}
      onSave={(input, moving) => save(m, input, moving)}
      onCancel={() => setEditingId(null)}
      onDelete={m ? () => void remove(m) : undefined}
      onConvert={m && m.variant !== 'split' && m.variant !== 'arc' && m.cueCount === 0 && m.objectIds.length > 0 ? () => void convert(m) : undefined}
      onUnsplit={m && m.variant === 'split' ? () => void unsplit(m) : undefined}
    />
  );

  if (mech.status === 'loading') {
    return <div className="editor-empty muted"><Icon icon={LoaderCircle} spin /> 載入機關中…</div>;
  }
  if (mech.status === 'error' && mechanisms.length === 0) {
    return (
      <div className="editor-empty" role="alert">
        <div className="muted">機關載入失敗</div>
        <button className="btn btn--ghost" style={{ marginTop: 8 }} onClick={() => void mech.refreshDefs()}>重新載入</button>
      </div>
    );
  }

  /** 「這個 cue 的機關位置」裡一個機關那一列 */
  const cueRow = (m: Mechanism) => (
    <MechanismCueRow
      key={m.id}
      mech={m}
      resolved={mech.selectedRow[m.id] ?? { value: m.defaultValue, stored: m.defaultValue, source: 'default', fromCueId: null, clamped: false }}
      editingDefault={editingDefault}
      cueName={cueName}
      check={cueCheck?.items[m.id]}
      front={front}
      focused={mech.focusedId === m.id}
      disabled={busy}
      onFocus={() => { if (mech.focusedId !== m.id) mech.setFocusedId(m.id); }}
      onCommit={(v) => void mech.commitValue(m.id, v)}
      onPreview={(v) => mech.previewValue(m.id, v)}
      onClear={() => void mech.clearValue(m.id)}
      timing={selectedCue ? mech.timings.get(selectedCue.id)?.[m.id] ?? null : null}
      showTiming={!!selectedCue && mech.selectedRow[m.id]?.source === 'own' && (!!library || !!cueCheck?.items[m.id]?.prevCueId)}
      cueSpan={cueCheck?.items[m.id]?.span ?? null}
      cueLabel={cueLabel}
      onTiming={selectedCue ? (patch) => void mech.commitTiming(selectedCue.id, m.id, patch) : undefined}
    />
  );

  // ── 吊掛（v2 第 4 階段） ──
  const msPerMeter = 1 / (scaleCorrection > 0 ? scaleCorrection : 1);
  const rigDef = (rigId: string) => mech.hoistRigs.find(r => r.rigId === rigId);
  /** 整組現在的傾斜字和說明（拖滑桿時照畫面上現在的高度；沒在拖就用這個 cue 的檢查，含走過來路上的說明） */
  const rigView = (rig: Mechanism, hoists: Mechanism[]) => {
    const def = rigDef(rig.id);
    if (!def) return null;
    const h = def.hoistIds.map(id => (mech.targets[id] ?? hoists.find(x => x.id === id)?.defaultValue ?? 0) * msPerMeter);
    const previewing = def.hoistIds.some(id => id in mech.preview);
    const stored = !previewing && selectedCue ? mech.hoistChecks?.get(selectedCue.id)?.get(rig.id) : undefined;
    return { tilt: tiltText(analyzeHoist(def.solver, h), front), notes: stored ? stored.warnings : hoistWarnings(def.solver, h, msPerMeter) };
  };

  // ── 「這個 cue 的機關位置」：跟導演面板同一種清單（分組標題 + 小方塊編號），點方塊才展開那個機關的控制 ──
  const cueGroups = panelGroups(mechanisms, () => true);
  const rowOf = (id: string) => mech.selectedRow[id];
  const isOwn = (id: string) => !editingDefault && rowOf(id)?.source === 'own';
  /** 點方塊 / 點單一機關的標題：展開它（再點收起），3D 也跟著選到 */
  const pickMech = (id: string) => {
    setOpenMechId(cur => (cur === id ? null : id));
    if (mech.focusedId !== id) mech.setFocusedId(id);
  };
  /** 展開的那一個機關的控制（滑桿、常用位置、打數字、回到沿用、延遲和走幾秒都在裡面） */
  const detailOf = (ids: string[]) => {
    const m = ids.includes(openMechId ?? '') ? mechanisms.find(x => x.id === openMechId) : undefined;
    return m ? <ul className="mech-rows mech-rows--member mech-rows--open" ref={detailRef}>{cueRow(m)}</ul> : null;
  };

  const cueGroupView = (g: PanelGroup<Mechanism>) => {
    if (g.type === 'single') {
      const m = g.def;
      return (
        <div key={g.key} className="adjust-group" data-mech-group={m.id}>
          <GroupTitle icon={SHAPE_ICON[mechShape(m)]} title={g.title} changed={isOwn(m.id)} on={openMechId === m.id}
            onClick={() => pickMech(m.id)} hint={openMechId === m.id ? '收起' : '展開這個機關的控制'} />
          {detailOf([m.id])}
        </div>
      );
    }
    if (g.type === 'rig') {
      const hoists = g.hoists;
      const tiles = tileLabels(hoists.map(h => h.name));
      const view = rigView(g.rig, hoists);
      return (
        <div key={g.key} className="adjust-group" data-mech-group={g.rig.id}>
          <div className="adjust-group__head">
            <GroupTitle icon={Anchor} title={g.title} changed={hoists.some(h => isOwn(h.id))} on={mech.focusedId === g.rig.id}
              onClick={() => mech.setFocusedId(g.rig.id)} hint="選這組吊掛" />
            {view && <span className="mech-hoist__tilt">{view.tilt}</span>}
            <button type="button" className="adjust-icon-btn" disabled={!selectedCue || busy || togetherFor === g.rig.id}
              aria-label="一起動" title={selectedCue ? '所有吊點一次拉到同一個高度（整片平著升降，像單點吊）' : '先在上面選一個 cue，位置是設在 cue 上的'}
              onClick={() => { setTogetherFor(g.rig.id); mech.setFocusedId(g.rig.id); }}><Icon icon={ArrowUp} /></button>
          </div>
          {!view && (
            <div className="mech-warn" role="status">
              <Icon icon={TriangleAlert} size="sm" className="mech-warn__icon" />
              <span>這組吊掛的吊點位置算不出來，物件不會動：按「設定機關」裡的「編輯」，把吊點分開（兩個吊點不能在同一個位置，三個以上不能排成一直線）</span>
            </div>
          )}
          {view?.notes.map(w => (
            <div key={w} className="mech-note" role="status">
              <Icon icon={Info} size="sm" className="mech-note__icon" />
              <span>{w}</span>
            </div>
          ))}
          {togetherFor === g.rig.id && selectedCue && (
            <RigTogether
              key={selectedCue.id}
              hoists={hoists}
              start={mean(hoists.map(h => mech.targets[h.id] ?? h.defaultValue))}
              disabled={busy}
              onApply={async (value) => {
                const items = hoists.map(h => ({ mechanismId: h.id, value: Math.min(h.travelMax, Math.max(h.travelMin, value)) }));
                if (items.some(it => it.value !== value)) toast.info('有吊點的行程到不了這個高度，已停在行程的邊界');
                if (await mech.applyPositions(selectedCue.id, items)) setTogetherFor(null);
              }}
              onClose={() => setTogetherFor(null)}
            />
          )}
          <Tiles members={hoists.map((def, i) => ({ def, tile: tiles[i] }))} isChanged={isOwn} isOn={(id) => openMechId === id}
            onPick={(_, id) => pickMech(id)} hint="" />
          {detailOf(hoists.map(h => h.id))}
        </div>
      );
    }
    const ids = g.members.map(x => x.def.id);
    const grp = g.groupId ? mech.groups.find(x => x.id === g.groupId) : undefined;
    const members = g.members.map(x => x.def);
    const range = valueRange(members.map(m => (editingDefault ? m.defaultValue : rowOf(m.id)?.value ?? m.defaultValue)));
    const kind = members[0]?.kind ?? 'linear';
    const firstCue = !!selectedCue && !members.some(m => cueCheck?.items[m.id]?.prevCueId);
    return (
      <div key={g.key} className="adjust-group" data-mech-group={ids[0]}>
        <div className="adjust-group__head">
          <GroupTitle icon={SHAPE_ICON[mechShape(members[0])]} title={g.title} changed={ids.some(isOwn)} on={false}
            onClick={() => pickMech(ids.includes(openMechId ?? '') ? openMechId! : ids[0])}
            hint={range ? `${members.length} 個機關 · ${formatMechValue(range.min, kind)}${range.max !== range.min ? ` ~ ${formatMechValue(range.max, kind)}` : ''}` : g.title} />
          {grp && (
            <button type="button" className="adjust-icon-btn" disabled={!selectedCue || busy || patternFor === grp.id}
              aria-label="套用花樣" title={selectedCue ? '一次設好這一排在這個 cue 的位置和延遲：一起、依序、波浪、漸層' : '先在上面選一個 cue，花樣是設在 cue 上的'}
              onClick={() => { setPatternFor(grp.id); mech.setFocusedId(members[0]?.id ?? null); }}><Icon icon={Waves} /></button>
          )}
        </div>
        {grp && patternFor === grp.id && selectedCue && (
          <MechanismPatternPanel
            key={selectedCue.id}
            group={grp}
            members={members}
            mech={mech}
            cueId={selectedCue.id}
            cueLabel={cueLabel(selectedCue.id)}
            isFirstCue={firstCue}
            disabled={busy}
            onClose={() => setPatternFor(null)}
          />
        )}
        <Tiles members={g.members} isChanged={isOwn} isOn={(id) => openMechId === id} onPick={(_, id) => pickMech(id)} hint="" />
        {detailOf(ids)}
      </div>
    );
  };


  /** 「設定機關」裡的一組吊掛：標題 + 編輯，展開後是每個吊點 */
  const setupRig = (rig: Mechanism, hoists: Mechanism[], index: number) => {
    const open = openSetup.has(rig.id);
    return (
      <li key={rig.id} className={'mech-setup-item mech-setup-group' + (mech.focusedId === rig.id || hoists.some(h => h.id === mech.focusedId) ? ' is-focused' : '')}>
        <div className="mech-setup-item__row" onClick={() => { setOpenSetup(s => toggle(s, rig.id)); mech.setFocusedId(rig.id); }}>
          <button type="button" className="mech-icon-btn" aria-expanded={open} aria-label={open ? `收起「${rig.name}」` : `展開「${rig.name}」`}
            onClick={e => { e.stopPropagation(); setOpenSetup(s => toggle(s, rig.id)); }}><Icon icon={open ? ChevronDown : ChevronRight} /></button>
          <div className="mech-setup-item__main">
            <span className="mech-setup-item__name"><Icon icon={Cable} size="sm" /> {rig.name}</span>
            <span className="muted small">吊掛 · {hoists.length} 個吊點 · {rig.objectIds.length} 個物件</span>
          </div>
          <div className="mech-setup-item__actions" onClick={e => e.stopPropagation()}>
            <button type="button" className="mech-icon-btn" title="上移" aria-label="上移" disabled={index === 0} onClick={() => move(index, -1)}><Icon icon={ArrowUp} /></button>
            <button type="button" className="mech-icon-btn" title="下移" aria-label="下移" disabled={index === layout.length - 1} onClick={() => move(index, 1)}><Icon icon={ArrowDown} /></button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setHoistFormFor(rig.id); setEditingId(null); setBatchOpen(false); setEditingGroupId(null); mech.setFocusedId(rig.id); }} disabled={hoistFormFor === rig.id}>
              <Icon icon={Pencil} /> 編輯吊掛
            </button>
          </div>
        </div>
        {hoistFormFor === rig.id && hoistForm(rig, hoists)}
        {open && (
          <ul className="mech-setup-list mech-setup-list--member">
            {hoists.map(h => (
              <li key={h.id} className={'mech-setup-item' + (mech.focusedId === h.id ? ' is-focused' : '')}>
                <div className="mech-setup-item__row" onClick={() => mech.setFocusedId(h.id)}>
                  <div className="mech-setup-item__main">
                    <span className="mech-setup-item__name">{h.name}</span>
                    <span className="muted small">
                      {formatMechValue(h.travelMin, 'linear', false)} ~ {formatMechValue(h.travelMax, 'linear')}
                      {` · ${h.cueCount} 個 cue 有設定`}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const hoistForm = (rig: Mechanism | null, hoists: Mechanism[]) => (
    <MechanismHoistForm
      key={rig?.id ?? 'new-rig'}
      editing={rig}
      hoists={hoists}
      mechanisms={mechanisms}
      stageObjects={stageObjects}
      selectedObjectIds={selectedObjectIds}
      scaleCorrection={scaleCorrection}
      front={front ?? { x: 0, y: 0, z: 1 }}
      groupBox={(ids) => mech.sceneQueryRef.current?.groupBox(ids) ?? null}
      busy={busy}
      onSave={(payload, moving) => save(rig, payload, moving)}
      onCancel={() => setHoistFormFor(null)}
      onDelete={rig ? () => void removeRig(rig, hoists) : undefined}
    />
  );

  /** 「設定機關」裡一個機關那一列（index = 在清單的第幾項；一排裡面的成員不能單獨上下移，傳 null） */
  const setupItem = (m: Mechanism, index: number | null) => {
    const kids = children(m);
    // 細節（滑過才看到）：行程、翻轉、弧形、幾個物件、幾個 cue 有設定、帶動誰
    const detail = [
      `${formatMechValue(m.travelMin, m.kind, false)} ~ ${formatMechValue(m.travelMax, m.kind)}`,
      flipLabel(m.metadata) || '',
      m.variant === 'arc' && m.arcRadius ? `沿弧形軌道（半徑 ${formatMechValue(m.arcRadius, 'linear')}）` : '',
      m.variant === 'split'
        ? `對開：A 邊 ${countSides(m.objectIds, m.objectSides ?? {}).a} 個、B 邊 ${countSides(m.objectIds, m.objectSides ?? {}).b} 個`
        : `${m.objectIds.length} 個物件`,
      `${m.cueCount} 個 cue 有設定`,
      kids.length > 0 ? `帶動：${kids.join('、')}` : '',
    ].filter(Boolean).join(' · ');
    return (
      <li key={m.id} className={'mech-setup-item' + (mech.focusedId === m.id ? ' is-focused' : '')}>
        <div className="mech-setup-item__row" onClick={() => mech.setFocusedId(m.id)} title={detail}>
          <div className="mech-setup-item__main">
            <span className="mech-setup-item__name">{m.name}</span>
            <span className="muted small">{formatMechValue(m.travelMin, m.kind, false)} ~ {formatMechValue(m.travelMax, m.kind)}{kids.length > 0 && ` · 帶動 ${kids.length} 個`}</span>
          </div>
          <div className="mech-setup-item__actions" onClick={e => e.stopPropagation()}>
            {index !== null && (
              <>
                <button type="button" className="mech-icon-btn" title="上移" aria-label="上移" disabled={index === 0} onClick={() => move(index, -1)}><Icon icon={ArrowUp} /></button>
                <button type="button" className="mech-icon-btn" title="下移" aria-label="下移" disabled={index === layout.length - 1} onClick={() => move(index, 1)}><Icon icon={ArrowDown} /></button>
              </>
            )}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setEditingId(m.id); mech.setFocusedId(m.id); }} disabled={editingId === m.id}>
              <Icon icon={Pencil} /> 編輯
            </button>
          </div>
        </div>
        {editingId === m.id && formFor(m)}
      </li>
    );
  };

  /** 「設定機關」裡的一排：標題 + 編輯這一排（名字、共用設定、解散、刪除），展開後是每個成員 */
  const setupGroup = (g: MechanismGroup, members: Mechanism[], index: number) => {
    const open = openSetup.has(g.id);
    const first = members[0];
    return (
      <li key={g.id} className={'mech-setup-item mech-setup-group' + (members.some(m => m.id === mech.focusedId) ? ' is-focused' : '')}>
        <div className="mech-setup-item__row" onClick={() => setOpenSetup(s => toggle(s, g.id))}>
          <button type="button" className="mech-icon-btn" aria-expanded={open} aria-label={open ? `收起「${g.name}」` : `展開「${g.name}」`}
            onClick={e => { e.stopPropagation(); setOpenSetup(s => toggle(s, g.id)); }}><Icon icon={open ? ChevronDown : ChevronRight} /></button>
          <div className="mech-setup-item__main">
            <span className="mech-setup-item__name">{g.name}</span>
            <span className="muted small">
              一排 {members.length} 個機關
              {first && ` · ${formatMechValue(first.travelMin, first.kind, false)} ~ ${formatMechValue(first.travelMax, first.kind)}`}
            </span>
          </div>
          <div className="mech-setup-item__actions" onClick={e => e.stopPropagation()}>
            <button type="button" className="mech-icon-btn" title="上移" aria-label="上移" disabled={index === 0} onClick={() => move(index, -1)}><Icon icon={ArrowUp} /></button>
            <button type="button" className="mech-icon-btn" title="下移" aria-label="下移" disabled={index === layout.length - 1} onClick={() => move(index, 1)}><Icon icon={ArrowDown} /></button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setEditingGroupId(g.id); setEditingId(null); setBatchOpen(false); }} disabled={editingGroupId === g.id}>
              <Icon icon={Pencil} /> 編輯這一排
            </button>
          </div>
        </div>
        {editingGroupId === g.id && (
          <MechanismGroupForm
            key={g.id}
            group={g}
            members={members}
            busy={busy}
            onSave={(patch) => saveGroup(g, patch)}
            onCancel={() => setEditingGroupId(null)}
            onDissolve={() => void dissolveGroup(g, members.length)}
            onDelete={() => void deleteGroup(g, members)}
          />
        )}
        {open && <ul className="mech-setup-list mech-setup-list--member">{members.map(m => setupItem(m, null))}</ul>}
      </li>
    );
  };

  return (
    <div className="mech-tab">
      {mech.check?.orderMismatch && (
        <div className="mech-warn mech-warn--song" role="status">
          <Icon icon={TriangleAlert} size="sm" className="mech-warn__icon" />
          <span>cue 的順序跟影片時間對不上：機關會從影片時間的上一個 cue 出發</span>
        </div>
      )}

      <section className="mech-section">
        <header className="mech-section__head">
          <h3 className="mech-section__title">機關位置{!library && <> · <span className="mech-section__cue">{title}</span></>}</h3>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {libTip.button}
            {!library && <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => void exportSheet()}
              disabled={exporting || mechanisms.length === 0}
              title="匯出機關總表：用 Excel 打開；整個專案每首歌、每個 cue、每個機關一行"
            ><Icon icon={exporting ? LoaderCircle : FileSpreadsheet} spin={exporting} /> 匯出總表</button>}
            <HelpLink anchor={HELP_ANCHORS.prodMechanisms} />
          </span>
        </header>
        {libTip.body}
        {!library && cues.length > 0 && (
          <label className="mech-cue-pick">
            <span className="muted small">換 cue</span>
            <select value={selectedCue?.id ?? ''} onChange={e => onSelectCue(e.target.value || null)} aria-label="換 cue">
              <option value="">預設位置（沒選 cue）</option>
              {masters.map((c, i) => <option key={c.id} value={c.id}>Cue {i + 1}「{c.name}」</option>)}
              {cues.filter(c => c.status !== 'master').map(c => <option key={c.id} value={c.id}>提案「{c.name}」</option>)}
            </select>
          </label>
        )}

        {mechanisms.length === 0 ? (
          <div className="editor-empty mech-empty">
            <Icon icon={Cog} size="xl" className="editor-empty__icon" />
            <div>還沒有機關</div>
            <small className="muted">升降台、轉台、滑軌這類會動的機械，先在下面「新增機關」設定一次，之後每個 cue 選位置就好</small>
          </div>
        ) : (
          <div className="adjust-groups mech-adjust-groups">
            {cueGroups.map(cueGroupView)}
          </div>
        )}
      </section>

      {!library && <section className="mech-section">
        <header className="mech-section__head">
          <h3 className="mech-section__title">設定機關</h3>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setHoistFormFor('new'); setBatchOpen(false); setEditingId(null); setEditingGroupId(null); }} disabled={hoistFormFor === 'new'}
              title="好幾個吊點拉到不同的高度，整片物件就會傾斜（飛行、吊掛的燈架 / 螢幕）">
              <Icon icon={Cable} /> 新增吊掛
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setBatchOpen(true); setEditingId(null); setEditingGroupId(null); setHoistFormFor(null); }} disabled={batchOpen}
              title="用 3D 畫面選取的好幾個物件，一個物件一個機關，編號、名稱、方向、行程一次設好">
              <Icon icon={Rows3} /> 一次建立多個
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setEditingId('new'); setBatchOpen(false); setHoistFormFor(null); }} disabled={editingId === 'new'}>
              <Icon icon={Plus} /> 新增機關
            </button>
          </span>
        </header>
        {scaleCorrection === 1 && (
          <div className="mech-warn" role="status">
            <Icon icon={TriangleAlert} size="sm" className="mech-warn__icon" />
            <span>還沒做測距校準：機關的行程是用公尺算的，先用工具列的測距把尺寸校準好，畫面上移動的距離才準</span>
          </div>
        )}
        {editingId === 'new' && formFor(null)}
        {hoistFormFor === 'new' && hoistForm(null, [])}
        {batchOpen && (
          <MechanismBatchForm
            mechanisms={mechanisms}
            groups={mech.groups}
            stageObjects={stageObjects}
            selectedObjectIds={selectedObjectIds}
            front={front ?? { x: 0, y: 0, z: 1 }}
            groupCenter={(ids) => mech.sceneQueryRef.current?.groupCenter(ids) ?? null}
            groupBox={(ids) => mech.sceneQueryRef.current?.groupBox(ids) ?? null}
            busy={busy}
            onSave={saveBatch}
            onCancel={() => setBatchOpen(false)}
          />
        )}
        {mechanisms.length > 0 && (
          <ul className="mech-setup-list">
            {layout.map((it, i) => (it.type === 'single' ? setupItem(it.mech, i) : it.type === 'rig' ? setupRig(it.rig, it.hoists, i) : setupGroup(it.group, it.members, i)))}
          </ul>
        )}
      </section>}
    </div>
  );
}

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

/** 一組吊掛的「一起動」：所有吊點一次拉到同一個高度（整片平著升降，像單點吊）；存成這個 cue 的位置，復原一步全退回去 */
function RigTogether({ hoists, start, disabled, onApply, onClose }: {
  hoists: Mechanism[];
  start: number;
  disabled: boolean;
  onApply: (value: number) => Promise<void>;
  onClose: () => void;
}) {
  const [text, setText] = useState(() => formatFieldNumber(start, 3));
  const [msg2, setMsg2] = useState<string | null>(null);
  const run = () => {
    const v = parseNumberInput(text);
    if (v === null) { setMsg2('高度要是數字'); return; }
    setMsg2(null);
    void onApply(v);
  };
  return (
    <div className="mech-hoist__together" role="group" aria-label="所有吊點一起動">
      <span>全部拉到</span>
      <label className="num-field">
        <input type="text" inputMode="decimal" aria-label="所有吊點拉到幾公尺" value={text} autoFocus
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); run(); } else if (e.key === 'Escape') onClose(); }} />
        <span className="num-field__unit" aria-hidden="true">m</span>
      </label>
      <button type="button" className="btn btn--primary btn--sm" disabled={disabled} onClick={run}>套用到 {hoists.length} 個吊點</button>
      <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>取消</button>
      {msg2 && <span className="field-error small" role="alert">{msg2}</span>}
    </div>
  );
}
